import Configuration from '../../../util/Configuration'
import { mapActions, mapGetters } from 'vuex'
import validationMixin from '../utils/validationMixin'

// tag field works in various contexts such as application, tracker type, etc...
// the prop context defines the tag operating context, for example
//
// tag specific to application scrum and tracker type epic
// "context": { "applicationId": "scrum", "trackerTypeId": "epic", "tagTypeId": "epictag"}
//
// tag specific to sales and shared by opportunity and proposition
// "context": { "applicationId": "sales", "trackerTypeId": "opportunity", "tagTypeId": "salestag"}
// "context": { "applicationId": "sales", "trackerTypeId": "proposition", "tagTypeId": "salestag"}
//
// generic global tag
// "context": { "applicationId": "scrum", "tagTypeId": "scrumtag"}

export default {
  name: 'FormTag',
  mixins: [validationMixin],

  props: {
    label: String,
    name: String,
    value: [String, Array],
    disabled: Boolean,
    options: Array,
    context: Object,
    multiple: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      innerValue: typeof this.value === 'string' ? [this.value] : this.value, // propagating v-model to the wrapped input (1/2)
      items: Array.isArray(this.options) ? this.options : [],
      loadingItems: false,
      loadFailure: false,
      tags: []
    }
  },

  computed: {
    ...mapGetters({
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      trackerTypeId: 'contextStore/trackerTypeId'
    }),

    computedContext () {
      return {
        applicationId: this.context?.applicationId || this.applicationId,
        trackerTypeId: this.context?.trackerTypeId || this.trackerTypeId,
        tagTypeId: this.context?.tagTypeId || this.trackerTypeId
      }
    },

    selectedContext () {
      return {
        applicationId: this.computedContext.applicationId,
        workspaceId: this.workspaceId,
        trackerTypeId: this.computedContext.trackerTypeId
      }
    },

    loadFailureMessage () {
      return 'Cannot load tags'
    }
  },

  created () {
    if (this.innerValue === null && this.defaultValue) {
      this.innerValue = this.defaultValue
    }
    this.fetchTags()
  },

  watch: {
    innerValue (val) { // propagating v-model to the wrapped input (2/2)
      if (this.multiple === false && this.innerValue.length > 1) {
        this.innerValue.splice(0, val.length - 1)
        return
      }
      this.$emit('input', val)
    },

    value (newValue) {
      this.innerValue = newValue // react to changes in input prop
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm'
    }),

    async fetchTags () {
      this.loadingItems = true

      try {
        const url = this.$axios.agilisUrlTags(
          this.computedContext.applicationId,
          this.computedContext.trackerTypeId,
          this.computedContext.tagTypeId
        )
        const config = this.$axios.agilisRequestConfig(
          url,
          null,
          {
            applicationId: this.computedContext.applicationId
          }
        )
        const response = await this.$axios.get(url, config)
        this.tags = response.data
        this.items = this.sortItems(response.data.map(t => t.tag))
      } catch (e) {
        this.loadFailure = true
        this.notifyError(e)
      }

      this.loadingItems = false
    },

    userCanAccessField (operation) {
      return Configuration.auth.userCanAccessField(
        this.selectedContext,
        this.name,
        null,
        operation
      )
    },

    async pushTag (tag) {
      if (!this.userCanAccessField('add')) {
        this.innerValue = this.innerValue.map(v => v.toUpperCase().trim())
        return
      }

      tag = tag.toUpperCase().trim()
      // messo questo blur per rimuovere il focus della combox e vedere il pop-up
      this.$refs.combobox.blur()
      const confirm = await this.notifyUserConfirm({
        title: this.$t('label.createTagTitle'),
        messages: this.$t('label.createTagMessagge', { tagName: tag }),
        confirm: this.$t('label.deleteYes'),
        cancel: this.$t('label.deleteNo')
      })

      if (!confirm) return

      this.loadingItems = true

      try {
        this.items = this.sortItems([tag].concat(this.items)) // prima di await cosi da nn perdersi input sequenziali prima della riposta dell'API

        const url = this.$axios.agilisUrlTags(
          this.computedContext.applicationId,
          this.computedContext.trackerTypeId,
          this.computedContext.tagTypeId
        )
        await this.$axios.post(url, { tag }, this.$axios.agilisRequestConfig(url))
        await this.fetchTags()
        this.innerValue = this.innerValue.map(value => {
          return value.toUpperCase().trim()
        })
      } catch (e) {
        this.notifyError(e)

        // Remove failed token form the array of items
        const indexItem = this.items.findIndex(i => i === tag)
        this.items.splice(indexItem, 1)

        // Remove failed token form the array of selected values
        const indexInnerValue = this.innerValue.findIndex(i => i === tag)
        this.innerValue.splice(indexInnerValue, 1)
      }

      this.loadingItems = false
    },

    async deleteTag (tag) {
      this.loadingItems = true

      try {
        // Remove tag form the array of items prima della riposta dell'API
        const indexItem = this.items.findIndex(i => i === tag)
        this.items.splice(indexItem, 1)

        const url = this.$axios.agilisUrlTagDelete(
          this.computedContext.applicationId,
          this.computedContext.trackerTypeId,
          this.computedContext.tagTypeId,
          this.tags.find(t => t.tag === tag).id
        )
        await this.$axios.delete(url, { ...this.$axios.agilisRequestConfig(url) })
        await this.fetchTags()
      } catch (e) {
        // Add failed token form the array of items
        this.items = this.sortItems([tag].concat(this.items))
      }

      this.loadingItems = false
    },

    sortItems (items) {
      const _items = items.slice(0)
      _items.sort((a, b) => a.toLowerCase() > b.toLowerCase() ? 1 : -1)
      return _items
    },

    onChange (newValue) {
      const lastToken = newValue.length > 0 && newValue[newValue.length - 1]

      if (lastToken && !this.items.find(i => i.toUpperCase().trim() === lastToken.toUpperCase().trim())) {
        this.pushTag(lastToken)
      } else if (this.value.length > newValue.length) {
        const removedTag = this.value.find(t => !newValue.includes(t))
        this.handleTagRemoval(removedTag, newValue)
      } else {
        this.innerValue = this.innerValue.map(value => {
          return value.toUpperCase().trim()
        })
      }
    },

    async handleTagRemoval (tag, newValue) {
      if (!this.userCanAccessField('delete')) return
      tag = tag.toUpperCase().trim()

      const existsInList = this.items.some(
        i => i.toUpperCase().trim() === tag
      )

      // If not in backend, just remove silently
      if (!existsInList) {
        return
      }

      // It exists, prompt user what to do
      this.$refs.combobox.blur()

      const confirm = await this.notifyUserConfirm({
        title: this.$t('label.deleteTagTitle'),
        messages: this.$t('label.deleteTagMessage', { tagName: tag }),
        confirm: this.$t('label.deleteYes'),
        cancel: this.$t('label.deleteNo')
      })

      if (confirm) {
        await this.deleteTag(tag)
      }
    },

    onInput (newValue) {
      if (newValue?.length > 0) {
        newValue[newValue.length - 1] = newValue[newValue.length - 1].toUpperCase().trim()
      }
    }
  }
}
